/**
 * WebGL (PixiJS) effect implementations.
 *
 * These classes are data-only: each holds the per-effect state that gets
 * packed into instanced GPU buffers or Pixi sprite pools each frame.
 * Rendering is done by WebGLRenderer._syncPixiEffects / _syncPixiMidTopEffects.
 *
 * buildWebGLEffectFactories(renderer) returns the factory map for
 * effectSystem.setFactories(); factories push directly to the renderer's
 * internal arrays so the effects never touch effectSystem.effects.
 */

import {
    FIREWORKS_RGB, FIREWORKS_DURATION, EXPLOSION_END_RGB,
    ICON_EFFECT_RGB, POWER_IMPACT_DURATION,
} from '../constants.js';

/** @param {number} c */
function _toHex2(c) { return Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'); }
export { _toHex2 };

/** Instanced explosion (shader-rendered). */
export class WebGLExplosion {
    /**
     * @param {number} duration
     * @param {number} x
     * @param {number} y
     * @param {number} startSize  unused (kept for signature parity)
     * @param {number} finalSize
     * @param {[number,number,number]} [_startRgb]  unused — start colour baked into shader
     * @param {[number,number,number]} [endRgb]
     */
    constructor(duration, x, y, startSize, finalSize, _startRgb, endRgb) {
        this.duration  = duration;
        this.time      = 0;
        this.x         = x;
        this.y         = y;
        this.finalSize = finalSize;
        this.seed      = Math.random() * 100;
        const [or, og, ob] = endRgb || EXPLOSION_END_RGB;
        this.or = or / 255; this.og = og / 255; this.ob = ob / 255;
    }

    /** @param {number} dt @returns {boolean} true when done */
    advance(dt) { this.time += dt; return this.time >= this.duration; }

    /** @returns {number} */
    get progress() { return Math.min(1.0, this.time / this.duration); }

    /**
     * Write this effect's data into the instanced explosion buffer.
     * @param {Float32Array} data  Flat instanced buffer (EXPLOSION_FLOATS per effect)
     * @param {number} base  Starting index (nExp * EXPLOSION_FLOATS)
     */
    pack(data, base) {
        data[base]   = this.x;
        data[base+1] = this.y;
        data[base+2] = this.finalSize;
        data[base+3] = this.progress;
        data[base+4] = this.seed;
        data[base+5] = this.or; data[base+6] = this.og; data[base+7] = this.ob;
    }
}

/** Instanced fireworks burst (shader-rendered). */
export class WebGLFireworks {
    /**
     * @param {number} x
     * @param {number} y
     * @param {[number,number,number]} colorRgb
     * @param {number} [duration]
     */
    constructor(x, y, colorRgb, duration = FIREWORKS_DURATION) {
        this.x        = x;
        this.y        = y;
        this.colorRgb = colorRgb || FIREWORKS_RGB;
        this.duration = duration;
        this.time     = 0;
        this.seed     = Math.random() * 100;
    }

    /** @param {number} dt @returns {boolean} true when done */
    advance(dt) { this.time += dt; return this.time >= this.duration; }

    /** @returns {number} */
    get progress() { return Math.min(1.0, this.time / this.duration); }

    /**
     * Write this effect's data into the instanced fireworks buffer.
     * @param {Float32Array} data  Flat instanced buffer (FIREWORKS_FLOATS per effect)
     * @param {number} base  Starting index (nFw * FIREWORKS_FLOATS)
     * @param {number} radius  World-unit quad half-radius (FIREWORKS_RADIUS constant)
     */
    pack(data, base, radius) {
        data[base]   = this.x;
        data[base+1] = this.y;
        data[base+2] = radius;
        data[base+3] = this.progress;
        data[base+4] = this.seed;
        data[base+5] = this.colorRgb[0] / 255;
        data[base+6] = this.colorRgb[1] / 255;
        data[base+7] = this.colorRgb[2] / 255;
    }
}

/** Spinning tinted-SVG icon effect (Pixi Sprite). */
export class WebGLIconEffect {
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {number[]} colorRgb
     * @param {number} duration
     * @param {string} layer
     * @param {number} startScale
     * @param {string} iconSrc
     * @param {number} rotations
     */
    constructor(x, y, radius, colorRgb, duration, layer, startScale, iconSrc, rotations) {
        this.x          = x;
        this.y          = y;
        this.radius     = radius;
        this.colorRgb   = colorRgb || ICON_EFFECT_RGB;
        this.duration   = duration;
        this.time       = 0;
        this.layer      = layer || 'top';
        this.startScale = startScale ?? 0.5;
        this.iconSrc    = iconSrc || '';
        this.rotations  = rotations ?? 2;
        /** @type {import('pixi.js').Sprite|null} */
        this._sprite = null;
    }

    get colorHex() {
        return `#${_toHex2(this.colorRgb[0])}${_toHex2(this.colorRgb[1])}${_toHex2(this.colorRgb[2])}`;
    }

    /** @returns {number} */
    get progress() { return Math.min(1, this.time / this.duration); }

    /** @param {number} dt @returns {boolean} true when done */
    advance(dt) { this.time += dt; return this.time >= this.duration; }

    /**
     * Create or update this effect's Pixi Sprite for the current frame.
     * @param {import('pixi.js').Container} groundContainer
     * @param {import('pixi.js').Container} topContainer
     * @param {(src: string, hex: string) => HTMLCanvasElement|null} getTintedImage
     * @param {(canvas: HTMLCanvasElement) => import('pixi.js').Texture} texFromCanvas
     * @param {typeof import('pixi.js').Sprite} Sprite
     * @returns {boolean} false if the image is not yet loaded (caller should skip this frame)
     */
    syncSprite(groundContainer, topContainer, getTintedImage, texFromCanvas, Sprite) {
        const canvas = getTintedImage(this.iconSrc, this.colorHex);
        if (!canvas) return false;

        if (!this._sprite) {
            this._sprite = new Sprite();
            this._sprite.anchor.set(0.5);
            const layer = this.layer === 'ground' ? groundContainer : topContainer;
            layer.addChild(this._sprite);
        }
        const tex = texFromCanvas(canvas);
        if (this._sprite.texture !== tex) this._sprite.texture = tex;

        const t     = this.progress;
        const alpha = t < 0.7 ? 1.0 : (1.0 - t) / 0.3;
        const size  = this.radius * 2 * (this.startScale + (1 - this.startScale) * t);
        const angle = t * Math.PI * 2 * this.rotations;

        this._sprite.x        = this.x;
        this._sprite.y        = this.y;
        this._sprite.width    = size;
        this._sprite.height   = size;
        this._sprite.alpha    = Math.max(0, alpha);
        this._sprite.rotation = angle;
        this._sprite.visible  = true;
        return true;
    }

    /** Remove and destroy the Pixi Sprite when the effect ends. */
    cleanup() {
        if (this._sprite) {
            if (this._sprite.parent) this._sprite.parent.removeChild(this._sprite);
            this._sprite.destroy({ texture: false });
            this._sprite = null;
        }
    }
}

/** Fading filled-circle power impact (Pixi Graphics). */
export class WebGLPowerImpact {
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {string} color  CSS color string
     */
    constructor(x, y, radius, color) {
        this.x        = x;
        this.y        = y;
        this.radius   = radius;
        this.color    = color;
        this.duration = POWER_IMPACT_DURATION;
        this.time     = 0;
    }

    /** @param {number} dt @returns {boolean} true when done */
    advance(dt) { this.time += dt; return this.time >= this.duration; }

    /** @returns {number} */
    get alpha() { return Math.max(0, 1 - this.time / this.duration); }

    /**
     * Draw the fading circle onto a Pixi Graphics object.
     * @param {import('pixi.js').Graphics} graphics
     */
    drawCircle(graphics) {
        graphics.circle(this.x, this.y, this.radius).fill({ color: this.color, alpha: this.alpha });
    }
}

/**
 * Build the factory map for WebGL mode.
 * Factories push directly into the renderer's typed effect arrays — effects
 * never touch effectSystem.effects (which stays empty in WebGL mode).
 *
 * @param {{ _explosions: WebGLExplosion[], _fireworks: WebGLFireworks[], _iconEffects: WebGLIconEffect[], _powerImpacts: WebGLPowerImpact[] }} renderer
 * @returns {Record<string, (...args: unknown[]) => null>}
 */
export function buildWebGLEffectFactories(renderer) {
    return {
        Explosion: (...a) => {
            renderer._explosions.push(new WebGLExplosion(...a));
            return null;
        },
        Fireworks: (...a) => {
            renderer._fireworks.push(new WebGLFireworks(...a));
            return null;
        },
        IconEffect: (...a) => {
            renderer._iconEffects.push(new WebGLIconEffect(...a));
            return null;
        },
        PowerImpactCircle: (...a) => {
            renderer._powerImpacts.push(new WebGLPowerImpact(...a));
            return null;
        },
    };
}
